import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonSpinner,
  IonText,
  IonButtons,
  IonButton,
  IonIcon,
  useIonRouter,
} from '@ionic/react';
import {
  settingsOutline,
  mailOutline,
  shieldCheckmarkOutline,
  createOutline,
} from 'ionicons/icons';
import { useUserAccount } from '../../../hooks/useUserAccount';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { ProfileAvatar } from './components/ProfileAvatar';
import { ChannelProfileList } from '../channels/components/ChannelProfileList';
import { hasAdminRole } from '../../admin/adminAccess';
import { CareerHistorySection } from './components/CareerHistorySection';
import { CareerStatusLine } from './components/CareerStatusLine';
import { EditPersonalDataModal } from './components/EditPersonalDataModal';
import { profileNameLines } from './publicProfileName';

const FieldLabel = ({ children }: { children: string }) => (
  <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-slate-400">
    {children}
  </div>
);

export const ProfileTab = () => {
  const { userAccount, loading, error, refetch } = useUserAccount();
  const { isEnabled } = useFeatureFlags();
  const ionRouter = useIonRouter();
  const [isEditingPersonalData, setIsEditingPersonalData] = useState(false);
  const socialEnabled = isEnabled(FeatureFlag.SOCIAL);

  const handleSettingsClick = () => {
    ionRouter.push('/app/main/perfil/configuracion');
  };

  const handleAdminClick = () => {
    ionRouter.push('/app/admin', 'forward');
  };

  const settingsButton = (
    <IonButtons slot="end">
      <IonButton onClick={handleSettingsClick} data-testid="settings-button">
        <IonIcon icon={settingsOutline} />
      </IonButton>
    </IonButtons>
  );

  if (loading) {
    return (
      <IonPage>
        <AppSectionHeader variant="main">
          <IonToolbar>
            <IonTitle className="ml-4">Perfil</IonTitle>
            {settingsButton}
          </IonToolbar>
        </AppSectionHeader>
        <IonContent className="ion-padding">
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <AppSectionHeader variant="main">
          <IonToolbar>
            <IonTitle className="ml-4">Perfil</IonTitle>
            {settingsButton}
          </IonToolbar>
        </AppSectionHeader>
        <IonContent className="ion-padding">
          <IonText color="danger">
            <p>Error al cargar el perfil</p>
          </IonText>
        </IonContent>
      </IonPage>
    );
  }

  if (!userAccount) {
    return (
      <IonPage>
        <AppSectionHeader variant="main">
          <IonToolbar>
            <IonTitle className="ml-4">Perfil</IonTitle>
            {settingsButton}
          </IonToolbar>
        </AppSectionHeader>
        <IonContent className="ion-padding">
          <IonText>
            <p>No se encontró información del perfil</p>
          </IonText>
        </IonContent>
      </IonPage>
    );
  }

  const nameLines = profileNameLines(userAccount, '');
  const hasName = nameLines.primary !== '';

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonTitle className="ml-4">Perfil</IonTitle>
          {settingsButton}
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {hasAdminRole(userAccount.roles) && (
          <IonButton expand="block" className="mb-3.5" onClick={handleAdminClick}>
            <IonIcon slot="start" icon={shieldCheckmarkOutline} />
            Acceder a Administración
          </IonButton>
        )}

        {/* Identity: compact header with a small avatar, name and email */}
        <div className="rounded-2xl border border-[#e8ebf1] bg-white p-4">
          <div className="flex items-center gap-4">
            <ProfileAvatar userAccount={userAccount} refetch={refetch} size="sm" />
            <div className="min-w-0 flex-1">
              <h2
                className="truncate font-heading text-gray-900"
                style={{ margin: 0, fontSize: '18px', fontWeight: 800, lineHeight: 1.2 }}
              >
                {nameLines.primary || userAccount.email}
              </h2>
              {nameLines.secondary && (
                <p
                  className="m-0 mt-0.5 truncate text-[15px] text-slate-600"
                  data-testid="profile-secondary-name"
                >
                  {nameLines.secondary}
                </p>
              )}
              {hasName && (
                <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-sm text-slate-500">
                  <IonIcon icon={mailOutline} className="shrink-0 text-base" />
                  <span className="truncate">{userAccount.email}</span>
                </div>
              )}
            </div>
          </div>
          {socialEnabled && <CareerStatusLine careerStatus={userAccount.careerStatus} />}
        </div>

        {/* Personal data (name, surname, display name), editable from a modal */}
        <div className="mt-3.5 rounded-2xl border border-[#e8ebf1] bg-white p-4">
          <div className="-mt-2 -mr-2 flex items-center justify-between gap-2">
            <h3 className="m-0 font-heading text-[15px] font-extrabold text-gray-900">
              Datos personales
            </h3>
            {socialEnabled && (
              <IonButton
                fill="clear"
                size="small"
                onClick={() => setIsEditingPersonalData(true)}
                aria-label="Editar datos personales"
                data-testid="edit-personal-data"
              >
                <IonIcon slot="icon-only" icon={createOutline} />
              </IonButton>
            )}
          </div>
          {userAccount.firstName || userAccount.lastName || socialEnabled ? (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-4">
              {userAccount.firstName && (
                <div>
                  <FieldLabel>Nombre</FieldLabel>
                  <p className="m-0 mt-0.5 text-[15px] font-semibold text-gray-900">
                    {userAccount.firstName}
                  </p>
                </div>
              )}
              {userAccount.lastName && (
                <div>
                  <FieldLabel>Apellido</FieldLabel>
                  <p className="m-0 mt-0.5 text-[15px] font-semibold text-gray-900">
                    {userAccount.lastName}
                  </p>
                </div>
              )}
              {socialEnabled && (
                <div className="col-span-2">
                  <FieldLabel>Conocido como</FieldLabel>
                  <p className="m-0 mt-0.5 text-[15px] font-semibold text-gray-900">
                    {userAccount.displayName || '-'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="m-0 mt-2 text-sm text-slate-400">Añade tu nombre y apellido</p>
          )}
        </div>
        {socialEnabled && (
          <EditPersonalDataModal
            isOpen={isEditingPersonalData}
            onClose={() => setIsEditingPersonalData(false)}
            userAccount={userAccount}
          />
        )}

        {isEnabled(FeatureFlag.POSTS) && <ChannelProfileList />}
        {socialEnabled && <CareerHistorySection />}
      </IonContent>
    </IonPage>
  );
};
